//
// PLEASE PAY ATTENTION TO https://www.w3.org/WAI/WCAG21/Techniques/general/G65
//
import { sanitizeTitle } from "../../utils/customSanitize";
import styles from "./Breadcrumbs.module.scss";
import { ParentsType } from "../../wp/getParentPages";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomLink } from "../CustomLink";
import { SmallCarousel } from "./SmallCarousel/SmallCarousel";

export interface BreadcrumbProps {
  breadcrumbPositon:
    | "is-justify-content-flex-start"
    | "is-justify-content-center"
    | "is-justify-content-flex-end";
  showOnMobile?: boolean;
  parents: ParentsType;
  extraClass?: string;
  breadcrumbTitle?: string;
}

export const Breadcrumbs = ({
  breadcrumbPositon = "is-justify-content-flex-start",
  parents,
  showOnMobile = true,
  extraClass = "",
  breadcrumbTitle,
}: BreadcrumbProps) => {
  const { getLabel } = useGlobalContext();
  /**
   * Render all breadcrumb <li> items. Reused by mobile carousel and desktop list.
   * - Adds "embla-slide" to <li> when used inside the carousel.
   * - Keeps original behaviors (span when no URL, sanitizeTitle, etc.).
   */
  const renderItems = (isCarousel: boolean) => {
    const liClass = isCarousel ? "embla-slide" : undefined;

    // Root / Home
    const items: React.ReactNode[] = [
      <li className={liClass} key="root">
        <CustomLink
          href={`${process.env.NEXT_PUBLIC_PUBLIC_URL}`}
          target={"_self"}
          className={styles.item}
        >
          {getLabel("breadcrumb_root")}
        </CustomLink>
      </li>,
    ];

    const unemptyParentPage = parents.filter((p) => !p.isEmptyParentPage);

    unemptyParentPage.forEach((el, i) => {
      const isCurrent = i === unemptyParentPage.length - 1;

      const title = isCurrent && breadcrumbTitle ? breadcrumbTitle : el.title;

      const removeStrongTitle = title
        ?.replace?.(/<strong>/g, "")
        .replace(/<\/strong>/g, "")
        .replace(/<br>/g, " ")
        .replace(/<\/br>/g, " ");

      const key = el.url ?? i;

      // ✅ G65: current page is NOT a link, uses aria-current="page"
      if (isCurrent) {
        items.push(
          <li className={liClass} aria-current="page" key={key}>
            <span
              className={styles.item}
              dangerouslySetInnerHTML={{
                __html: sanitizeTitle(removeStrongTitle),
              }}
            />
          </li>,
        );
        return;
      }

      // Previous crumbs: links when URL exists, otherwise text
      items.push(
        <li className={liClass} key={key}>
          {el.url ? (
            <CustomLink href={el.url} target="_self" className={styles.item}>
              {removeStrongTitle}
            </CustomLink>
          ) : (
            <span
              className={styles.item}
              dangerouslySetInnerHTML={{
                __html: sanitizeTitle(removeStrongTitle),
              }}
            />
          )}
        </li>,
      );
    });

    return items;
  };

  return (
    <nav
      aria-label="Breadcrumbs"
      className={`${styles.breadcrumbs} ${!showOnMobile ? "is-hidden-mobile" : ""} ${extraClass}`}
    >
      {/* Touch: carousel */}
      <SmallCarousel extraClassName="is-hidden-desktop">
        {renderItems(true)}
      </SmallCarousel>

      {/* Desktop: list */}
      <ul className={`${breadcrumbPositon} is-hidden-touch`}>
        {renderItems(false)}
      </ul>
    </nav>
  );
};
